import HardwareOption from 'IndexedDB/models/HardwareOption';
import GapSize from 'IndexedDB/models/GapSize';
import ProductSize from 'IndexedDB/models/ProductSize';
import HandlerBuilder from 'shared/service/HandlerBuilder';
import {CabinetTop} from 'shared/types/CabinetTop';
import Axios from './Axios';
import {store} from 'store/customer/storeSetup';
import {RoomApi} from 'components/customer/Room/store/roomApi';
import {RoomUpdateResponse} from 'components/customer/Room/types/RoomUpdate';

type GetCabinetTopType = {
    availableTops?: number[];
    showNone?: boolean;
};

export const getCabinetTop = async ({
    availableTops = [],
    showNone = false,
}: GetCabinetTopType): Promise<CabinetTop[]> => {
    const {data: cabinetTops} = await store.dispatch(
        RoomApi.endpoints.getCabinetTops.initiate()
    );

    if (availableTops.length && cabinetTops?.length) {
        const filteredTops = cabinetTops.filter((cabinetTop) =>
            availableTops.includes(cabinetTop.id)
        );

        if (showNone) {
            filteredTops.unshift({
                id: -1,
                name: 'None',
            });
        }

        return filteredTops;
    }

    return cabinetTops;
};

export const getHardwareOptions = (): Promise<HardwareOption[]> => {
    const handlerBuilder = HandlerBuilder.createInstance<HardwareOption>(
        'customer/manufacturerHardwareOptions'
    );

    return handlerBuilder
        .setStoreName('manufacturer_hardware_options')
        .setResultName('hardware_options')
        .build()
        .getData();
};

export const getSupplyMethods = async (): Promise<HardwareOption[]> => {
    const supplyMethods: HardwareOption[] = [];
    const hardwareOptions = await getHardwareOptions();

    Array.isArray(hardwareOptions) &&
        hardwareOptions.forEach((option) => {
            option.id = option.value;

            if (option.assembly) {
                supplyMethods.push(option);
            }
        });

    return supplyMethods;
};

export const getHardwareMethods = async (): Promise<HardwareOption[]> => {
    const supplyHardwares: HardwareOption[] = [];
    const hardwareOptions = await getHardwareOptions();

    Array.isArray(hardwareOptions) &&
        hardwareOptions.forEach((option) => {
            option.id = option.value;

            if (option.supplyHardware) {
                supplyHardwares.push(option);
            }
        });

    return supplyHardwares;
};

export const getAdjustableLegsMethods = async (): Promise<HardwareOption[]> => {
    const adjustableLegsMethods: HardwareOption[] = [];
    const hardwareOptions = await getHardwareOptions();

    Array.isArray(hardwareOptions) &&
        hardwareOptions.forEach((option) => {
            option.id = option.value;

            if (option.adjLegs) {
                adjustableLegsMethods.push(option);
            }
        });

    return adjustableLegsMethods;
};

export const getGapSizeDefaults = (resetCache = false): Promise<GapSize[]> => {
    const handlerBuilder = HandlerBuilder.createInstance<GapSize>(
        'rooms/gap-size-defaults'
    );
    return handlerBuilder
        .setStoreName('gap_sizes_defaults')
        .setResetCache(resetCache)
        .build()
        .getData();
};

export const getProductSizeDefaults = (
    resetCache = false
): Promise<ProductSize[]> => {
    const handlerBuilder = HandlerBuilder.createInstance<ProductSize>(
        'rooms/product-size-defaults'
    );
    return handlerBuilder
        .setStoreName('product_sizes_defaults')
        .setResetCache(resetCache)
        .build()
        .getData();
};

export const saveRoom = async (
    data: FormData,
    jobId: number,
    roomId?: number
): Promise<RoomUpdateResponse> => {
    let url = `jobs/${jobId}/rooms`;

    if (roomId) url += `/${roomId}`;
    const response = await Axios.instance().post<RoomUpdateResponse>(
        url,
        data,
        {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        }
    );

    if (
        !response.hasOwnProperty('data') ||
        !response.data.hasOwnProperty('success') ||
        !response.data.success
    ) {
        throw new Error('Room settings could not be saved.');
    }

    return response.data;
};
